import { CSRF_HEADER_NAME, PARTNER_CSRF_COOKIE_NAME } from '@shared/constants/index.ts';
import type { ApiErrorBody } from '@shared/types/index.ts';
import type { ServerPublicKeyCreation, ServerPublicKeyRequest, WebauthnAssertion } from '../lib/webauthn.ts';

/** Browser client for the partner portal. Its cookies and CSRF token are
 * intentionally separate from tenant and operator sessions. */
const PARTNER_API_BASE = '/api/partner';

export class PartnerApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, body: ApiErrorBody | null) {
    super(body?.error?.message || `The partner API answered ${status}.`);
    this.name = 'PartnerApiError';
    this.status = status;
    this.code = body?.error?.code ?? 'UNKNOWN';
  }
}

/**
 * Called whenever the portal API reports the session is gone.
 *
 * Without it an expired or revoked cookie leaves the shell rendering: the
 * session query answered successfully five minutes ago, going stale does not
 * itself refetch, and no child page's 401 tells it otherwise — so a partner
 * clicks between screens that each show their own error and never reaches the
 * sign-in page. The tenant client carries the same handler for the same
 * reason (see `setUnauthenticatedHandler` in api/client.ts).
 */
let onUnauthenticated: (() => void) | null = null;

export function setPartnerUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

/**
 * Called when the portal refuses a request because this deployment mandates a
 * second factor that this session has not proved.
 *
 * Separate from the 401 handler because the answer is different: the session
 * is fine, and what is needed is the security screen rather than the sign-in
 * page.
 */
let onMfaSetupRequired: (() => void) | null = null;

export function setPartnerMfaSetupHandler(handler: (() => void) | null): void {
  onMfaSetupRequired = handler;
}

function csrfToken(): string | null {
  for (const part of document.cookie.split(';')) {
    const index = part.indexOf('=');
    if (index !== -1 && part.slice(0, index).trim() === PARTNER_CSRF_COOKIE_NAME) return part.slice(index + 1).trim();
  }
  return null;
}

async function request<T>(
  path: string,
  options: { method?: 'GET' | 'POST' | 'DELETE'; body?: unknown } = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };
  if (method !== 'GET') {
    const csrf = csrfToken();
    if (csrf) headers[CSRF_HEADER_NAME] = csrf;
  }
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  const response = await fetch(`${PARTNER_API_BASE}${path}`, {
    method,
    headers,
    credentials: 'same-origin',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });
  const payload = response.headers.get('content-type')?.includes('application/json')
    ? await response.json().catch(() => null)
    : null;
  if (!response.ok) {
    const error = new PartnerApiError(response.status, payload as ApiErrorBody | null);
    // The session question itself is excluded: a signed-out visitor asking who
    // they are is the normal answer there, not a session that just died, and
    // firing the handler on it would fight the query that owns the state.
    if (response.status === 401 && path !== '/session') onUnauthenticated?.();
    // The mandate's own refusal. The shell decides where a partner goes from
    // here, so it needs to hear about it — the session flag and this 403 are
    // derived from the same rule but read at different moments, and a page
    // that simply printed the message would be a dead end with no way out.
    if (response.status === 403 && error.code === 'MFA_SETUP_REQUIRED' && path !== '/session') {
      onMfaSetupRequired?.();
    }
    throw error;
  }
  // A successful DELETE deliberately answers 204. There is no envelope to
  // unwrap, and treating that as an error would falsely tell the partner that
  // a method could not be removed after the server had already removed it.
  if (response.status === 204) return undefined as T;
  return (payload as { data: T }).data;
}

export type PartnerChallengeOption = {
  methodId: string | null;
  type: 'totp' | 'webauthn' | 'recovery';
  label: string;
};

export type PartnerSecurityStatus = {
  enabled: boolean;
  required: boolean;
  methods: Array<{
    id: string;
    type: string;
    label: string;
    createdAt: string;
    lastUsedAt: string | null;
  }>;
  availableMethods: string[];
  recoveryCodesRemaining: number;
  freshAuth: { fresh: boolean; expiresAt: string | null };
};

type PartnerSession = { mfaSetupRequired?: boolean };

export type PartnerIdentity = {
  partner: { id: string; displayName: string; status: string; slug: string; url: string };
  user: { id: string; email: string; fullName: string };
  mfa: {
    /** The deployment's master switch. Off means no factor can be enrolled. */
    enabled: boolean;
    enrolled: boolean;
    /** Whether this deployment MANDATES a factor. Off by default. */
    required: boolean;
    setupRequired: boolean;
  };
};

export type PartnerDashboard = {
  partner: {
    id: string;
    displayName: string;
    status: string;
    slug: string;
    url: string;
    paypalEmailOnFile: boolean;
    taxDocumentationOnFile: boolean;
    /** Whether missing paperwork actually holds this deployment's payouts. */
    taxDocumentationRequired: boolean;
  };
  tier: {
    currentRatePercent: number;
    negotiated: boolean;
    activePayingCustomers: number;
    nextRatePercent: number | null;
    customersToNextTier: number | null;
    maximumRatePercent: number | null;
  };
  customers: { total: number; activePaying: number; notYetPaying: number };
  earnings: {
    period: string;
    estimatedCommissionCents: number;
    eligibleRevenueCents: number;
    payableBalanceCents: number;
    lifetimeCommissionCents: number;
    lifetimePaidCents: number;
    minimumPayoutCents: number;
    nextPayoutDay: number;
    /** What every figure above is denominated in. */
    currency: string;
  };
};

export type PartnerCustomer = {
  id: string;
  name: string;
  signedUpAt: string;
  status: string;
  plan: string | null;
  recurringValueCents: number;
  eligibleRevenueCents: number;
  commissionCents: number;
  attributionSource: string;
  campaign: string | null;
};

export type PartnerCommissionEntry = {
  id: string;
  type: string;
  companyId: string | null;
  eligibleRevenueCents: number;
  ratePercent: number | null;
  amountCents: number;
  currency: string;
  finalized: boolean;
  /** The payout BATCH this row belongs to — not proof it was settled. */
  payoutId: string | null;
  reason: string | null;
  createdAt: string;
};

export type PartnerPayout = {
  id: string;
  amountCents: number;
  currency: string;
  status: string;
  holdReason: string | null;
  carriedForwardCents: number;
  sentAt: string | null;
  completedAt: string | null;
};

export type PartnerAttributionRequest = {
  id: string;
  customerEmail: string;
  businessName: string | null;
  status: string;
  decisionReason: string | null;
  createdAt: string;
  decidedAt: string | null;
};

export type PartnerAccount = {
  id: string;
  displayName: string;
  contactName: string;
  contactEmail: string;
  slug: string;
  url: string;
  status: string;
  paypalEmailMasked: string | null;
  taxDocumentationOnFile: boolean;
};

export type PartnerLoginResponse =
  | (PartnerSession & { mfaRequired?: false })
  | {
      mfaRequired: true;
      loginToken: string;
      options: PartnerChallengeOption[];
      trustedDeviceDays: number;
    };

export function isPartnerMfaChallenge(
  response: PartnerLoginResponse,
): response is Extract<PartnerLoginResponse, { mfaRequired: true }> {
  return response.mfaRequired === true;
}

export const partnerApi = {
  login: (body: { email: string; password: string }) =>
    request<PartnerLoginResponse>('/auth/login', { method: 'POST', body }),
  loginChallenge: (body: { loginToken: string; type: PartnerChallengeOption['type']; methodId?: string | null }) =>
    request<{
      challengeId: string;
      type: string;
      publicKey?: ServerPublicKeyRequest;
    }>('/auth/mfa/challenge', { method: 'POST', body }),
  loginVerify: (body: {
    loginToken: string;
    challengeId: string;
    code?: string;
    assertion?: WebauthnAssertion;
    rememberDevice?: boolean;
  }) => request<PartnerSession>('/auth/mfa/verify', { method: 'POST', body }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST', body: {} }),
  /**
   * Who is signed in — the one call the portal shell makes before anything
   * else. Answers even where a mandate is unmet, because the screen that
   * resolves that state is inside the shell.
   */
  session: () => request<PartnerIdentity>('/session'),
  dashboard: () => request<PartnerDashboard>('/dashboard'),
  customers: () =>
    request<{
      total: number;
      truncated: boolean;
      /** What this partner's earnings are denominated in. */
      currency: string;
      /** What the deployment bills subscriptions in — labels the MRR column. */
      subscriptionCurrency: string;
      customers: PartnerCustomer[];
    }>('/customers'),
  commissions: (period?: string) =>
    request<{
      period: string;
      netCents: number;
      eligibleRevenueCents: number;
      currency: string;
      entryCount: number;
      truncated: boolean;
      entries: PartnerCommissionEntry[];
    }>(period ? `/commissions?period=${encodeURIComponent(period)}` : '/commissions'),
  payouts: () => request<{ payouts: PartnerPayout[] }>('/payouts'),
  attributionRequests: () => request<{ requests: PartnerAttributionRequest[] }>('/attribution-requests'),
  createAttributionRequest: (body: {
    customerEmail: string;
    businessName?: string;
    approximateSignupDate?: string;
    explanation: string;
  }) => request<{ id: string; status: string }>('/attribution-requests', { method: 'POST', body }),
  account: () => request<PartnerAccount>('/account'),
  updatePayoutAccount: (paypalEmail: string) =>
    request<{ paypalEmailMasked: string }>('/account/payout-account', {
      method: 'POST',
      body: { paypalEmail },
    }),
  updateSlug: (slug: string) =>
    request<{ slug: string; url: string }>('/account/slug', { method: 'POST', body: { slug } }),
};

export const partnerSecurityApi = {
  status: () => request<PartnerSecurityStatus>('/security'),
  enrollTotp: () =>
    request<{ methodId: string; secret: string; uri: string }>('/security/totp/enroll', { method: 'POST', body: {} }),
  verifyTotp: (methodId: string, code: string) =>
    request<{ recoveryCodes: string[] | null }>('/security/totp/verify', {
      method: 'POST',
      body: { methodId, code },
    }),
  securityKeyOptions: () =>
    request<{ challengeId: string; publicKey: ServerPublicKeyCreation }>('/security/security-keys/register/options', {
      method: 'POST',
      body: {},
    }),
  registerSecurityKey: (body: {
    challengeId: string;
    label: string;
    clientDataJson: string;
    attestationObject: string;
    transports?: string[];
  }) =>
    request<{ methodId: string; recoveryCodes: string[] | null }>('/security/security-keys/register', {
      method: 'POST',
      body,
    }),
  removeMethod: (id: string) => request<void>(`/security/methods/${id}`, { method: 'DELETE' }),
  regenerateRecoveryCodes: () =>
    request<{ codes: string[] }>('/security/recovery-codes/regenerate', {
      method: 'POST',
      body: {},
    }),
  elevateOptions: () => request<{ options: PartnerChallengeOption[] }>('/security/elevate/options'),
  elevateChallenge: (body: { type: PartnerChallengeOption['type']; methodId?: string | null }) =>
    request<{
      challengeId: string;
      type: string;
      publicKey?: ServerPublicKeyRequest;
    }>('/security/elevate/challenge', {
      method: 'POST',
      body,
    }),
  elevateVerify: (body: { challengeId: string; code?: string; assertion?: WebauthnAssertion }) =>
    request<{ verified: true; fresh: boolean; expiresAt: string | null }>('/security/elevate/verify', {
      method: 'POST',
      body,
    }),
};
